import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw, Settings } from "lucide-react";

const AntSimulator = () => {
  const canvasRef = useRef(null);
  const [scenario, setScenario] = useState(3);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gridSize, setGridSize] = useState(3);
  const [animSpeed, setAnimSpeed] = useState(5);
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState({ total: 0, meetings: 0 });
  const [showTrails, setShowTrails] = useState(true);
  const [meetingStatus, setMeetingStatus] = useState(null);
  const [fastSimCount, setFastSimCount] = useState(100);
  const [isSimulating, setIsSimulating] = useState(false);
  const [showAnimationInSim, setShowAnimationInSim] = useState(false);
  const [currentSimProgress, setCurrentSimProgress] = useState(0);
  const [showMeetingPoints, setShowMeetingPoints] = useState(false);
  const [meetingPointsData, setMeetingPointsData] = useState({});
  const [uniquePaths, setUniquePaths] = useState(new Set());
  
  const stopSimulationRef = useRef(false);
  const currentBatchPathsRef = useRef(null);
  const aliceGridRef = useRef({ x: 0, y: 0 });
  const bobGridRef = useRef({ x: 0, y: 0 });
  const alicePixelRef = useRef({ x: 100, y: 500 });
  const bobPixelRef = useRef({ x: 500, y: 100 });
  const stateRef = useRef({ finished: false, hasMetOnce: false });
  const trailsRef = useRef({ alice: [], bob: [] });
  const animationRef = useRef(null);

  const canvasSize = 600;
  const padding = 100;
  const gridWidth = 400;

  const calculateCombination = (n, k) => {
    if (k > n) return 0;
    if (k === 0 || k === n) return 1;
    
    let result = 1;
    for (let i = 1; i <= k; i++) {
      result = result * (n - k + i) / i;
    }
    return Math.round(result);
  };

  const gridToPixel = (gx, gy) => {
    const cellSize = gridWidth / gridSize;
    return {
      x: padding + gx * cellSize,
      y: padding + gy * cellSize
    };
  };

  const pathToString = (path) => {
    return path.map(([x, y]) => `${x},${y}`).join('->');
  };

  const recordPath = (path) => {
    const pathStr = pathToString(path);
    
    const pathsToCheck = currentBatchPathsRef.current || uniquePaths;
    const isNewPath = !pathsToCheck.has(pathStr);
    
    if (currentBatchPathsRef.current) {
      currentBatchPathsRef.current.add(pathStr);
    }
    
    setUniquePaths(prev => {
      const newSet = new Set(prev);
      newSet.add(pathStr);
      return newSet;
    });
    
    return isNewPath;
  };

  const recordMeetingPoint = (gridX, gridY) => {
    const key = `${gridX},${gridY}`;
    setMeetingPointsData(prev => ({
      ...prev,
      [key]: (prev[key] || 0) + 1
    }));
  };

  const chooseDirection = (gx, gy, isAlice) => {
    if (isAlice) {
      const canRight = gx < gridSize;
      const canUp = gy > 0;
      if (!canRight) return 1;
      if (!canUp) return 0;
      return Math.random() < 0.5 ? 0 : 1;
    } else {
      const canLeft = gx > 0;
      const canDown = gy < gridSize;
      if (!canLeft) return 1;
      if (!canDown) return 0;
      return Math.random() < 0.5 ? 0 : 1;
    }
  };

  const resetSimulation = () => {
    stateRef.current = { finished: false, hasMetOnce: false };
    setMeetingStatus(null);

    if (scenario === 1) {
      const alicePath = ["A", "B", "C"][Math.floor(Math.random() * 3)];
      const bobPath = ["A", "B", "C"][Math.floor(Math.random() * 3)];
      alicePixelRef.current = { x: 250, y: 200, path: alicePath };
      bobPixelRef.current = { x: 450, y: 200, path: bobPath };
      trailsRef.current = { alice: [], bob: [] };
    } else {
      aliceGridRef.current = { x: 0, y: gridSize };
      bobGridRef.current = { x: gridSize, y: 0 };
      
      const alicePos = gridToPixel(0, gridSize);
      const bobPos = gridToPixel(gridSize, 0);
      alicePixelRef.current = alicePos;
      bobPixelRef.current = bobPos;
      
      trailsRef.current = {
        alice: [[alicePos.x, alicePos.y]],
        bob: [[bobPos.x, bobPos.y]]
      };
    }
  };

  const stepSimulation = () => {
    if (scenario === 1) return;

    if (stateRef.current.finished || stateRef.current.hasMetOnce) {
      return;
    }

    const alice = aliceGridRef.current;
    const bob = bobGridRef.current;

    if (scenario === 3 && alice.x === bob.x && alice.y === bob.y) {
      stateRef.current.hasMetOnce = true;
      recordMeetingPoint(alice.x, alice.y);
      setMeetingStatus("success");
      setStats(prev => ({ total: prev.total + 1, meetings: prev.meetings + 1 }));
      setIsPlaying(false);
      return;
    }

    if (alice.x === gridSize && alice.y === 0) {
      if (!stateRef.current.finished) {
        stateRef.current.finished = true;
        if (scenario === 2) {
          const path = trailsRef.current.alice.map(([x, y]) => {
            const cellSize = gridWidth / gridSize;
            return [Math.round((x - padding) / cellSize), Math.round((y - padding) / cellSize)];
          });
          const isNewPath = recordPath(path);
          
          setMeetingStatus(isNewPath ? "success" : "duplicate");
          setStats(prev => ({ 
            total: prev.total + 1, 
            meetings: prev.meetings + 1 
          }));
          setIsPlaying(false);
        } else if (scenario === 3 && !stateRef.current.hasMetOnce) {
          setMeetingStatus("failed");
          setStats(prev => ({ total: prev.total + 1, meetings: prev.meetings }));
          setIsPlaying(false);
        }
      }
    } else {
      const direction = chooseDirection(alice.x, alice.y, true);
      if (direction === 0) {
        alice.x++;
      } else {
        alice.y--;
      }
      
      const newPos = gridToPixel(alice.x, alice.y);
      alicePixelRef.current = newPos;
      trailsRef.current.alice.push([newPos.x, newPos.y]);
    }

    if (scenario === 3 && !stateRef.current.hasMetOnce && 
        alice.x === bob.x && alice.y === bob.y) {
      stateRef.current.hasMetOnce = true;
      recordMeetingPoint(alice.x, alice.y);
      setMeetingStatus("success");
      setStats(prev => ({ total: prev.total + 1, meetings: prev.meetings + 1 }));
      setIsPlaying(false);
      return;
    }

    if (scenario === 3) {
      if (bob.x === 0 && bob.y === gridSize) {
        if (!stateRef.current.finished) {
          stateRef.current.finished = true;
          if (!stateRef.current.hasMetOnce) {
            setMeetingStatus("failed");
            setStats(prev => ({ total: prev.total + 1, meetings: prev.meetings }));
            setIsPlaying(false);
          }
        }
      } else {
        const direction = chooseDirection(bob.x, bob.y, false);
        if (direction === 0) {
          bob.x--;
        } else {
          bob.y++;
        }
        
        const newPos = gridToPixel(bob.x, bob.y);
        bobPixelRef.current = newPos;
        trailsRef.current.bob.push([newPos.x, newPos.y]);
      }

      if (!stateRef.current.hasMetOnce && alice.x === bob.x && alice.y === bob.y) {
        stateRef.current.hasMetOnce = true;
        recordMeetingPoint(alice.x, alice.y);
        setMeetingStatus("success");
        setStats(prev => ({ total: prev.total + 1, meetings: prev.meetings + 1 }));
        setIsPlaying(false);
      }
    }
  };

  const updateScenario1 = () => {
    const targets = {
      A: { x: 350, y: 100 },
      B: { x: 350, y: 200 },
      C: { x: 350, y: 300 }
    };

    const moveTowards = (current, target, speed) => {
      const dx = target.x - current.x;
      const dy = target.y - current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < speed) {
        return { x: target.x, y: target.y, done: true };
      }

      return {
        x: current.x + (dx / dist) * speed,
        y: current.y + (dy / dist) * speed,
        done: false
      };
    };

    const aliceTarget = targets[alicePixelRef.current.path];
    const bobTarget = targets[bobPixelRef.current.path];

    const aliceResult = moveTowards(alicePixelRef.current, aliceTarget, 2);
    const bobResult = moveTowards(bobPixelRef.current, bobTarget, 2);

    alicePixelRef.current = { ...alicePixelRef.current, ...aliceResult };
    bobPixelRef.current = { ...bobPixelRef.current, ...bobResult };

    if (aliceResult.done && bobResult.done) {
      stateRef.current.finished = true;
      const met = alicePixelRef.current.path === bobPixelRef.current.path;
      setMeetingStatus(met ? "success" : "failed");
      setStats(prev => ({
        total: prev.total + 1,
        meetings: prev.meetings + (met ? 1 : 0)
      }));
      setIsPlaying(false);
    }
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    if (scenario === 1) {
      drawScenario1(ctx);
    } else {
      drawGrid(ctx);
      if (showTrails) drawTrails(ctx);
      drawAnts(ctx);
      if (showMeetingPoints) drawMeetingPoints(ctx);
    }
  };

  const drawScenario1 = (ctx) => {
    ctx.beginPath();
    ctx.ellipse(350, 200, 120, 80, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#e8f5e9";
    ctx.fill();
    ctx.strokeStyle = "#4caf50";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.strokeStyle = "#8d6e63";
    ctx.lineWidth = 3;
    [[250, 200, 350, 100], [250, 200, 350, 200], [250, 200, 350, 300],
     [450, 200, 350, 100], [450, 200, 350, 200], [450, 200, 350, 300]].forEach(([x1, y1, x2, y2]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });

    ctx.fillStyle = "#2e7d32";
    ctx.font = "bold 18px Arial";
    ctx.fillText("A", 370, 95);
    ctx.fillText("B", 370, 205);
    ctx.fillText("C", 370, 315);

    drawCircle(ctx, alicePixelRef.current.x, alicePixelRef.current.y, "#ff0000", "Alice");
    drawCircle(ctx, bobPixelRef.current.x, bobPixelRef.current.y, "#0000ff", "Bob");
  };

  const drawGrid = (ctx) => {
    const cellSize = gridWidth / gridSize;
    ctx.strokeStyle = "#bdbdbd";
    ctx.lineWidth = 1;

    for (let i = 0; i <= gridSize; i++) {
      const pos = padding + i * cellSize;
      ctx.beginPath();
      ctx.moveTo(pos, padding);
      ctx.lineTo(pos, padding + gridWidth);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(padding, pos);
      ctx.lineTo(padding + gridWidth, pos);
      ctx.stroke();
    }
  };

  const drawMeetingPoints = (ctx) => {
    for (const key in meetingPointsData) {
      const count = meetingPointsData[key];
      const [gx, gy] = key.split(',').map(Number);
      const pos = gridToPixel(gx, gy);
      
      ctx.fillStyle = "rgba(255, 165, 0, 0.3)";
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 20, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = "rgba(255, 140, 0, 0.8)";
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.fillStyle = "#ff8c00";
      ctx.font = "bold 14px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(count.toString(), pos.x, pos.y);
    }
    
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  };

  const drawTrails = (ctx) => {
    if (trailsRef.current.alice.length > 0) {
      ctx.strokeStyle = "rgba(255, 0, 0, 0.4)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      trailsRef.current.alice.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point[0], point[1]);
        else ctx.lineTo(point[0], point[1]);
      });
      ctx.stroke();
    }

    if (scenario === 3 && trailsRef.current.bob.length > 0) {
      ctx.strokeStyle = "rgba(0, 0, 255, 0.4)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      trailsRef.current.bob.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point[0], point[1]);
        else ctx.lineTo(point[0], point[1]);
      });
      ctx.stroke();
    }
  };

  const drawAnts = (ctx) => {
    drawCircle(ctx, alicePixelRef.current.x, alicePixelRef.current.y, "#ff0000", "Alice");

    if (scenario === 3) {
      drawCircle(ctx, bobPixelRef.current.x, bobPixelRef.current.y, "#0000ff", "Bob");
    } else if (scenario === 2) {
      const target = gridToPixel(gridSize, 0);
      ctx.fillStyle = "#ffa000";
      ctx.beginPath();
      ctx.arc(target.x, target.y, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#000";
      ctx.font = "20px Arial";
      ctx.fillText("🍰", target.x - 8, target.y + 8);
    }
  };

  const drawCircle = (ctx, x, y, color, label) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, 10, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#000";
    ctx.font = "10px Arial";
    if (label === "Alice") {
      ctx.fillText(label, x - 15, y + 25);
    } else {
      ctx.fillText(label, x - 10, y - 15);
    }
  };

  const runSingleSimulation = () => {
    if (scenario === 1) {
      const alicePath = ["A", "B", "C"][Math.floor(Math.random() * 3)];
      const bobPath = ["A", "B", "C"][Math.floor(Math.random() * 3)];
      return { success: alicePath === bobPath, meetingPoint: null, path: null };
    }

    let aliceX = 0, aliceY = gridSize;
    let bobX = gridSize, bobY = 0;
    let hasMetOnce = false;
    let meetingPoint = null;
    let path = [[0, gridSize]];
    let maxSteps = 1000;
    let steps = 0;

    while (steps < maxSteps) {
      steps++;

      if (aliceX === gridSize && aliceY === 0) {
        if (scenario === 2) return { success: true, meetingPoint: null, path };
        if (scenario === 3 && !hasMetOnce) return { success: false, meetingPoint: null, path: null };
        break;
      } else {
        const dir = chooseDirection(aliceX, aliceY, true);
        if (dir === 0) aliceX++;
        else aliceY--;
        path.push([aliceX, aliceY]);
      }

      if (scenario === 3) {
        if (bobX === 0 && bobY === gridSize) {
          if (!hasMetOnce) return { success: false, meetingPoint: null, path: null };
          break;
        } else {
          const dir = chooseDirection(bobX, bobY, false);
          if (dir === 0) bobX--;
          else bobY++;
        }

        if (!hasMetOnce && aliceX === bobX && aliceY === bobY) {
          hasMetOnce = true;
          meetingPoint = { x: aliceX, y: aliceY };
          return { success: true, meetingPoint, path: null };
        }
      }
    }

    return { success: hasMetOnce, meetingPoint, path: null };
  };

  const runFastSimulation = async () => {
    setIsSimulating(true);
    setIsPlaying(false);
    setCurrentSimProgress(0);
    stopSimulationRef.current = false;

    if (!showAnimationInSim) {
      let successCount = 0;
      const batchSize = 100;
      const totalBatches = Math.ceil(fastSimCount / batchSize);
      const tempMeetingPoints = {};
      const tempPaths = new Set(uniquePaths);

      for (let batch = 0; batch < totalBatches; batch++) {
        if (stopSimulationRef.current) break;
        
        const currentBatchSize = Math.min(batchSize, fastSimCount - batch * batchSize);
        for (let i = 0; i < currentBatchSize; i++) {
          if (stopSimulationRef.current) break;
          const result = runSingleSimulation();
          if (result.success) {
            successCount++;
            
            if (scenario === 2 && result.path) {
              const pathStr = pathToString(result.path);
              const isNewPath = !tempPaths.has(pathStr);
              if (isNewPath) {
                tempPaths.add(pathStr);
              }
            }
            
            if (result.meetingPoint) {
              const key = `${result.meetingPoint.x},${result.meetingPoint.y}`;
              tempMeetingPoints[key] = (tempMeetingPoints[key] || 0) + 1;
            }
          }
        }

        const actualTotal = Math.min((batch + 1) * batchSize, fastSimCount);
        setStats({ total: actualTotal, meetings: successCount });
        setCurrentSimProgress(actualTotal);
        
        if (scenario === 2) {
          setUniquePaths(new Set(tempPaths));
        }
        
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      if (Object.keys(tempMeetingPoints).length > 0) {
        setMeetingPointsData(prev => {
          const updated = { ...prev };
          for (const key in tempMeetingPoints) {
            updated[key] = (updated[key] || 0) + tempMeetingPoints[key];
          }
          return updated;
        });
      }
      
      if (scenario === 2) {
        setUniquePaths(tempPaths);
      }
    } else {
      currentBatchPathsRef.current = new Set(uniquePaths);
      
      let successCount = 0;
      
      for (let i = 0; i < fastSimCount; i++) {
        if (stopSimulationRef.current) break;
        
        resetSimulation();
        const result = await runAnimatedSimulation();
        if (result) successCount++;
        
        setStats({ total: i + 1, meetings: successCount });
        setCurrentSimProgress(i + 1);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      currentBatchPathsRef.current = null;
    }

    setIsSimulating(false);
    setMeetingStatus(null);
    setCurrentSimProgress(0);
  };

  const runAnimatedSimulation = () => {
    return new Promise((resolve) => {
      if (scenario === 1) {
        const animate = () => {
          updateScenario1();
          draw();
          
          if (stateRef.current.finished) {
            resolve(alicePixelRef.current.path === bobPixelRef.current.path);
          } else {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
        return;
      }

      const animate = () => {
        stepSimulation();
        draw();

        if (!stateRef.current.finished && !stateRef.current.hasMetOnce) {
          requestAnimationFrame(animate);
        } else {
          setTimeout(() => {
            resolve(stateRef.current.hasMetOnce);
          }, 100);
        }
      };
      requestAnimationFrame(animate);
    });
  };

  const stopSimulation = () => {
    stopSimulationRef.current = true;
  };

  useEffect(() => {
    if (!isPlaying) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    let frameCount = 0;
    const framesPerStep = Math.max(1, Math.round(31 - animSpeed * 3));

    const animate = () => {
      frameCount++;
      
      if (frameCount >= framesPerStep) {
        frameCount = 0;
        if (scenario === 1) {
          updateScenario1();
        } else {
          stepSimulation();
        }
      }
      
      draw();

      if (!stateRef.current.finished && !stateRef.current.hasMetOnce) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsPlaying(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, scenario, gridSize, animSpeed, showTrails, showMeetingPoints, meetingPointsData, uniquePaths]);

  useEffect(() => {
    resetSimulation();
    currentBatchPathsRef.current = null;
    setMeetingPointsData({});
    setUniquePaths(new Set());
    draw();
  }, [scenario, gridSize]);

  useEffect(() => {
    draw();
  }, [showMeetingPoints, meetingPointsData, uniquePaths]);

  const handleReset = () => {
    setIsPlaying(false);
    currentBatchPathsRef.current = null;
    resetSimulation();
    draw();
  };

  const handleScenarioChange = (newScenario) => {
    setIsPlaying(false);
    currentBatchPathsRef.current = null;
    setScenario(newScenario);
    setStats({ total: 0, meetings: 0 });
    setUniquePaths(new Set());
    setMeetingPointsData({});
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-gradient-to-br from-green-50 to-blue-50 min-h-screen">
      <h1 className="text-3xl font-bold text-center mb-6 text-green-800">
        🐜 蚂蚁相遇模拟器
      </h1>

      <div className="flex gap-4 mb-6 justify-center flex-wrap">
        <button
          onClick={() => handleScenarioChange(1)}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            scenario === 1 ? "bg-green-600 text-white shadow-lg" : "bg-white text-gray-700 hover:bg-green-100"
          }`}
        >
          场景1: 三岔路口
        </button>
        <button
          onClick={() => handleScenarioChange(2)}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            scenario === 2 ? "bg-blue-600 text-white shadow-lg" : "bg-white text-gray-700 hover:bg-blue-100"
          }`}
        >
          场景2: Alice寻找食物
        </button>
        <button
          onClick={() => handleScenarioChange(3)}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            scenario === 3 ? "bg-purple-600 text-white shadow-lg" : "bg-white text-gray-700 hover:bg-purple-100"
          }`}
        >
          场景3: 相向而行
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h3 className="font-bold text-lg mb-2">
          {scenario === 1 && "🔀 场景1：两只蚂蚁在三岔路口随机选择路径"}
          {scenario === 2 && "🎯 场景2：Alice寻找右上角的食物（统计所有不同的最短路径）"}
          {scenario === 3 && "↔️ 场景3：两只蚂蚁同时从对角出发（每个网格点随机选择方向）"}
        </h3>
        <p className="text-gray-600">
          {scenario === 1 && "两只蚂蚁各自随机选择A、B、C三条路，只有选择同一条路才能相遇。"}
          {scenario === 2 && "Alice从左下角出发，在每个网格交叉点随机选择向右或向上，统计发现了多少条不同的最短路径。"}
          {scenario === 3 && "两只蚂蚁速度相同同时出发，在每个交叉点随机选择方向，看是否会相遇。"}
        </p>
      </div>

      <div className="flex justify-center mb-6 relative">
        {meetingStatus && (
          <div className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-6 py-4 rounded-xl shadow-xl ${
            meetingStatus === "success" ? "bg-green-500 bg-opacity-90" : 
            meetingStatus === "duplicate" ? "bg-yellow-500 bg-opacity-90" :
            "bg-red-500 bg-opacity-90"
          }`}>
            <div className="flex items-center gap-3 text-white">
              <div className="text-3xl">
                {meetingStatus === "success" ? "🎉" : 
                 meetingStatus === "duplicate" ? "🔄" : "😢"}
              </div>
              <div>
                <div className="text-xl font-bold">
                  {meetingStatus === "success"
                    ? scenario === 2
                      ? "发现新路径！"
                      : "相遇成功！"
                    : meetingStatus === "duplicate"
                    ? "已走过的路径"
                    : "未能相遇"}
                </div>
                <div className="text-sm opacity-90">
                  {meetingStatus === "success"
                    ? scenario === 2
                      ? "Alice 找到了一条新的最短路径"
                      : "Alice 和 Bob 相遇了"
                    : meetingStatus === "duplicate"
                    ? "这条路径之前已经走过了"
                    : "他们错过了..."}
                </div>
              </div>
            </div>
          </div>
        )}

        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          className="border-4 border-gray-300 rounded-lg shadow-lg bg-amber-50"
        />
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex gap-4 justify-center mb-4 flex-wrap">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            disabled={isSimulating}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPlaying ? <><Pause size={20} /> 暂停</> : <><Play size={20} /> 开始</>}
          </button>
          <button
            onClick={handleReset}
            disabled={isSimulating}
            className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw size={20} /> 重置
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            disabled={isSimulating}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Settings size={20} /> 设置
          </button>
        </div>

        <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
          <h4 className="font-bold text-lg mb-3 text-purple-800">⚡ 快速批量模拟</h4>
          
          <div className="space-y-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">模拟次数:</label>
                <input
                  type="number"
                  min="10"
                  max="10000"
                  value={fastSimCount}
                  onChange={(e) => setFastSimCount(parseInt(e.target.value) || 100)}
                  disabled={isSimulating}
                  className="px-3 py-2 border-2 border-purple-300 rounded-lg w-28 focus:outline-none focus:border-purple-500 disabled:opacity-50"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showAnim"
                  checked={showAnimationInSim}
                  onChange={(e) => setShowAnimationInSim(e.target.checked)}
                  disabled={isSimulating}
                  className="w-4 h-4 disabled:opacity-50"
                />
                <label htmlFor="showAnim" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  显示动画过程
                </label>
              </div>
            </div>

            <div className="flex gap-4 items-center flex-wrap">
              <button
                onClick={runFastSimulation}
                disabled={isSimulating}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSimulating ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    模拟中... ({currentSimProgress}/{fastSimCount})
                  </>
                ) : (
                  <>🚀 开始快速模拟</>
                )}
              </button>
              
              {isSimulating && (
                <button
                  onClick={stopSimulation}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg font-semibold flex items-center gap-2"
                >
                  ⏹️ 停止
                </button>
              )}
              
              {isSimulating && (
                <div className="flex-1 min-w-[200px]">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${(currentSimProgress / fastSimCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-600 mt-3">
            💡 {showAnimationInSim 
              ? '开启动画模式后，可以看到每次模拟的过程（推荐10-50次）' 
              : '无动画模式会快速计算结果，适合大量统计（推荐1000-5000次）'}
          </p>
        </div>

        {showSettings && scenario !== 1 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                网格大小: {gridSize}×{gridSize}
              </label>
              <input
                type="range"
                min="1"
                max="8"
                value={gridSize}
                onChange={(e) => setGridSize(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">
                动画速度: {animSpeed}
                <span className="text-gray-500 text-xs ml-2">(数值越大越快)</span>
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={animSpeed}
                onChange={(e) => setAnimSpeed(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showTrails}
                onChange={(e) => setShowTrails(e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-sm font-semibold">显示运动轨迹</label>
            </div>
            {scenario === 3 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showMeetingPoints"
                  checked={showMeetingPoints}
                  onChange={(e) => setShowMeetingPoints(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="showMeetingPoints" className="text-sm font-semibold">
                  显示相遇点统计
                </label>
                {showMeetingPoints && Object.keys(meetingPointsData).length > 0 && (
                  <button
                    onClick={() => setMeetingPointsData({})}
                    className="ml-4 px-3 py-1 text-xs bg-orange-100 text-orange-600 rounded hover:bg-orange-200 transition-all"
                  >
                    清空相遇点
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-lg">📊 统计信息</h3>
          <button
            onClick={() => {
              setStats({ total: 0, meetings: 0 });
              setUniquePaths(new Set());
              currentBatchPathsRef.current = null;
            }}
            className="px-4 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200 transition-all"
          >
            清空统计
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">模拟次数</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded">
            <div className="text-2xl font-bold text-green-600">
              {scenario === 2 ? uniquePaths.size : stats.meetings}
            </div>
            <div className="text-sm text-gray-600">
              {scenario === 2 ? "不同路径数" : "相遇次数"}
            </div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded">
            <div className="text-2xl font-bold text-purple-600">
              {scenario === 2 
                ? (stats.total > 0 ? ((uniquePaths.size / stats.total) * 100).toFixed(1) : 0)
                : (stats.total > 0 ? ((stats.meetings / stats.total) * 100).toFixed(1) : 0)
              }%
            </div>
            <div className="text-sm text-gray-600">
              {scenario === 2 ? "新路径发现率" : "相遇概率"}
            </div>
          </div>
        </div>
        {scenario === 3 && showMeetingPoints && Object.keys(meetingPointsData).length > 0 && (
          <div className="mt-4 p-3 bg-orange-50 rounded border border-orange-200">
            <div className="text-sm font-semibold text-orange-800 mb-1">
              🎯 相遇点分布统计
            </div>
            <div className="text-xs text-gray-600">
              共有 {Object.keys(meetingPointsData).length} 个不同的相遇点，
              总相遇次数: {Object.values(meetingPointsData).reduce((a, b) => a + b, 0)} 次
            </div>
          </div>
        )}
        <div className="mt-4 text-sm text-gray-600">
          {scenario === 1 && "✨ 理论概率：1/3 ≈ 33.3%"}
          {scenario === 2 && `✨ ${gridSize}×${gridSize}网格的最短路径总数：C(${gridSize*2}, ${gridSize}) = ${calculateCombination(gridSize*2, gridSize)}条`}
          {scenario === 3 && "✨ 复杂概率：需要大量模拟来估算"}
        </div>
      </div>

      <div className="mt-4 text-center text-sm text-gray-600">
        💡 提示：使用"快速模拟"功能可快速运行大量实验来验证理论概率
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default AntSimulator;
